import { useEffect, useRef, useState } from "react";
import {
  type DailyReportRecoveryCandidateDto,
  type DriverDailyReportDto,
  DAILY_REPORT_INSPECTION_RESULT_LABELS,
  DAILY_REPORT_STATUS_LABELS_FOR_DRIVER,
  dailyReportAlcoholResultLabel,
  routeStatusLabel,
} from "@carry/contract";
import { Plus } from "lucide-react";
import { Button, Input, Textarea } from "@carry/ui";
import { deliveryStatusLabel, formatDisplayDate, formatYen, timeSlotLabel } from "@carry/shared";
import { Header } from "../components/Header";
import { IncompleteStopBackfillForm } from "../components/IncompleteStopBackfillForm";
import { MonthStrip } from "../components/MonthStrip";
import { ReportCard, ReportRow, ReportSectionTitle } from "../components/ReportCard";
import {
  getDriverDailyReport,
  isDriverDailyReportAlreadyExistsError,
  recoverDriverDailyReport,
  resubmitDriverDailyReport,
  useDriverDailyReportRecoveryCandidates,
  useDriverDailyReports,
  type DriverFetcher,
} from "../hooks";
import { formatHm, jstMonth } from "../lib/normalize";

const STATUS_CLASS: Record<DriverDailyReportDto["status"], string> = {
  draft: "bg-[var(--bg-sunken)] text-[var(--ink-3)]",
  submitted: "bg-[var(--st-pending-bg,#fef3c7)] text-[var(--st-pending-ink,#92400e)]",
  approved: "bg-[var(--st-done-bg)] text-[var(--st-done-ink)]",
  rejected: "bg-[var(--st-cancel-bg)] text-[var(--st-cancel-ink)]",
};

function StatusBadge({ status }: { status: DriverDailyReportDto["status"] }) {
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[length:var(--fs-xs)] font-semibold ${STATUS_CLASS[status]}`}
    >
      {DAILY_REPORT_STATUS_LABELS_FOR_DRIVER[status]}
    </span>
  );
}

function reportDistanceKm(report: DriverDailyReportDto): string | null {
  return report.actual_distance_km ?? report.total_distance_km;
}

/** 後追い入力の対象。skipped/cancelled は意図した終端なので上書きしない。 */
function isIncompleteStop(status: string): boolean {
  return status !== "completed" && status !== "skipped" && status !== "cancelled";
}

function ReportDetailView({
  report,
  fetcher,
  onBack,
  onEdit,
  onUpdated,
}: {
  report: DriverDailyReportDto;
  fetcher: DriverFetcher;
  onBack: () => void;
  onEdit: () => void;
  onUpdated: (updated: DriverDailyReportDto) => void;
}) {
  const canBackfill = report.status !== "approved";
  return (
    <div className="min-h-screen bg-[var(--bg)] pb-[var(--bottom-bar-clearance)]">
      <Header title="日報詳細" sub={formatDisplayDate(report.work_date)} showBack onBack={onBack} />
      <div className="px-3 py-3.5">
        <ReportSectionTitle label="基本情報" />
        <ReportCard>
          {[
            ["日付", formatDisplayDate(report.work_date)],
            ["状態", <StatusBadge key="status" status={report.status} />],
            ["配送件数", `${report.delivery_count} 件`],
            ["走行距離", reportDistanceKm(report) ? `${reportDistanceKm(report)} km` : "—"],
            ["出勤", report.clock_in_at ? formatHm(report.clock_in_at) : "—"],
            ["退勤", report.clock_out_at ? formatHm(report.clock_out_at) : "—"],
          ].map(([k, v], i, a) => (
            <ReportRow key={String(k)} last={i === a.length - 1}>
              <span className="text-[var(--ink-3)]">{k}</span>
              <span className="num font-medium">{v}</span>
            </ReportRow>
          ))}
        </ReportCard>

        {report.vehicle_number && (
          <>
            <ReportSectionTitle label="車両" className="mt-3.5" />
            <ReportCard>
              <ReportRow last>
                <span className="text-[var(--ink-3)]">車両番号</span>
                <span className="num font-medium">{report.vehicle_number}</span>
              </ReportRow>
            </ReportCard>
          </>
        )}

        <ReportSectionTitle label="点呼・点検" className="mt-3.5" />
        <ReportCard>
          {[
            [
              "アルコール検査",
              report.alcohol_result ? dailyReportAlcoholResultLabel(report.alcohol_result) : "—",
            ],
            [
              "車両点検",
              report.inspection_result
                ? DAILY_REPORT_INSPECTION_RESULT_LABELS[report.inspection_result]
                : "—",
            ],
            [
              "給油金額",
              report.refueling_amount_yen != null ? formatYen(report.refueling_amount_yen) : "—",
            ],
          ].map(([k, v], i, a) => (
            <ReportRow key={String(k)} last={i === a.length - 1}>
              <span className="text-[var(--ink-3)]">{k}</span>
              <span className="num font-medium">{String(v)}</span>
            </ReportRow>
          ))}
        </ReportCard>

        {report.delivery_history.length > 0 && (
          <>
            <ReportSectionTitle
              label="配送履歴"
              hint={`${report.delivery_history.length} 件`}
              className="mt-3.5"
            />
            <ReportCard pad={10}>
              {report.delivery_history.map((stop, i) => (
                <div
                  key={stop.stop_id}
                  className={`py-2 text-[length:var(--fs-sm)] ${
                    i < report.delivery_history.length - 1
                      ? "border-b border-[var(--rule-softer)]"
                      : ""
                  }`}
                >
                  <div className="font-medium">{stop.name ?? stop.address ?? "—"}</div>
                  <div className="text-[length:var(--fs-xs)] text-[var(--ink-3)]">
                    {deliveryStatusLabel(stop.status)}
                    {stop.actual_time ? ` · ${formatHm(stop.actual_time)}` : ""}
                    {stop.backfilled_at ? " · 後から登録" : ""}
                  </div>
                  {canBackfill &&
                    // #842: 差し戻された日報は、完了済み stop も実績を修正できる
                    // (差戻し理由が実績時刻の誤りでも直せないと堂々巡りになるため)。
                    (isIncompleteStop(stop.status) ||
                      (report.status === "rejected" && stop.status === "completed")) && (
                      <IncompleteStopBackfillForm
                        stopId={stop.stop_id}
                        isPickup={stop.stop_type === "pickup"}
                        stopName={stop.name}
                        workDate={report.work_date}
                        initialArrivalHint={stop.actual_time ? formatHm(stop.actual_time) : null}
                        initialDepartureHint={
                          stop.actual_departure ? formatHm(stop.actual_departure) : null
                        }
                        mode={isIncompleteStop(stop.status) ? "backfill" : "correct"}
                        fetcher={fetcher}
                        onSaved={async () =>
                          onUpdated(await getDriverDailyReport(fetcher, report.public_id))
                        }
                      />
                    )}
                </div>
              ))}
            </ReportCard>
          </>
        )}

        {report.remarks && (
          <>
            <ReportSectionTitle label="備考" className="mt-3.5" />
            <ReportCard>
              <p className="text-[length:var(--fs-sm)] text-[var(--ink-2)]">{report.remarks}</p>
            </ReportCard>
          </>
        )}

        {report.rejection_note && (
          <>
            <ReportSectionTitle label="差戻し理由" className="mt-3.5" />
            <ReportCard>
              <p className="text-[length:var(--fs-sm)] text-[var(--st-cancel-ink)]">
                {report.rejection_note}
              </p>
            </ReportCard>
          </>
        )}

        {report.status === "rejected" && (
          <Button type="button" onClick={onEdit} className="mt-4 w-full">
            修正して再提出
          </Button>
        )}
      </div>
    </div>
  );
}

export function HistoryScreen({
  fetcher,
  initialReportPublicId,
  initialWorkDate,
  onBack,
}: {
  fetcher: DriverFetcher;
  initialReportPublicId?: string;
  /** 配送先詳細からの誘導。対象月を開き、該当日の日報があれば詳細を出す */
  initialWorkDate?: string;
  onBack: () => void;
}) {
  const [month, setMonth] = useState<string>(() => initialWorkDate?.slice(0, 7) ?? jstMonth());
  const [selectedReport, setSelectedReport] = useState<DriverDailyReportDto | null>(null);
  const [editingReport, setEditingReport] = useState<DriverDailyReportDto | null>(null);
  const [initialReportError, setInitialReportError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const didAutoOpenWorkDate = useRef(false);

  const { reports, loading, error, refetch } = useDriverDailyReports({
    ...fetcher,
    month,
  });

  useEffect(() => {
    if (!initialReportPublicId || !fetcher.session) return;
    let cancelled = false;
    setInitialReportError(null);
    void getDriverDailyReport(fetcher, initialReportPublicId)
      .then((report) => {
        if (cancelled) return;
        setMonth(report.work_date.slice(0, 7));
        setSelectedReport(report);
      })
      .catch((cause: unknown) => {
        if (cancelled) return;
        setInitialReportError(
          cause instanceof Error ? cause.message : "差戻し日報の取得に失敗しました",
        );
      });
    return () => {
      cancelled = true;
    };
  }, [initialReportPublicId, fetcher.apiBaseUrl, fetcher.session, fetcher.fetchImpl]);

  useEffect(() => {
    if (!initialWorkDate || didAutoOpenWorkDate.current || selectedReport || creating) return;
    if (loading || reports == null) return;
    didAutoOpenWorkDate.current = true;
    const match = reports.find((report) => report.work_date === initialWorkDate);
    if (match) setSelectedReport(match);
  }, [initialWorkDate, reports, loading, selectedReport, creating]);

  if (creating) {
    return (
      <CreatePastReportForm
        fetcher={fetcher}
        month={month}
        onCancel={() => setCreating(false)}
        onDone={(report) => {
          setCreating(false);
          setSelectedReport(report);
          void refetch();
        }}
      />
    );
  }

  if (editingReport) {
    return (
      <ResubmitReportForm
        fetcher={fetcher}
        report={editingReport}
        onCancel={() => setEditingReport(null)}
        onDone={(updated) => {
          setEditingReport(null);
          setSelectedReport(updated);
          void refetch();
        }}
      />
    );
  }

  if (selectedReport) {
    return (
      <ReportDetailView
        report={selectedReport}
        fetcher={fetcher}
        onBack={() => setSelectedReport(null)}
        onEdit={() => setEditingReport(selectedReport)}
        onUpdated={(updated) => {
          setSelectedReport(updated);
          void refetch();
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg)] pb-[var(--bottom-bar-clearance)]">
      <Header title="過去日報" sub="月別一覧" showBack onBack={onBack} />
      <div className="px-3 pt-3">
        <Button
          type="button"
          onClick={() => setCreating(true)}
          variant="outline"
          className="flex w-full items-center justify-center gap-1.5"
        >
          <Plus size={16} /> 過去の日報を作成
        </Button>
      </div>
      <MonthStrip value={month} onChange={setMonth} />
      <div className="px-3 py-3.5">
        {loading && (
          <div className="p-4 text-center text-[length:var(--fs-xs)] text-[var(--ink-3)]">
            読込中…
          </div>
        )}
        {error && (
          <div className="rounded-lg bg-[var(--st-cancel-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-cancel-ink)]">
            ⚠ {error}
          </div>
        )}
        {initialReportError && (
          <div
            role="alert"
            className="rounded-lg bg-[var(--st-cancel-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-cancel-ink)]"
          >
            ⚠ {initialReportError}
          </div>
        )}
        {reports && reports.length === 0 && (
          <div className="p-4 text-center text-[length:var(--fs-xs)] text-[var(--ink-3)]">
            履歴はありません
          </div>
        )}
        {reports?.map((report) => (
          <div
            key={report.public_id}
            data-testid="report-card-row"
            role="button"
            tabIndex={0}
            onClick={() => setSelectedReport(report)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") setSelectedReport(report);
            }}
            className="mb-1.5 cursor-pointer rounded-lg bg-[var(--bg-raised)] px-3.5 py-2.5 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <span className="text-[length:var(--fs-sm)] font-medium">
                {formatDisplayDate(report.work_date)}
              </span>
              <StatusBadge status={report.status} />
            </div>
            <div className="mt-1 flex gap-3 text-[length:var(--fs-xs)] text-[var(--ink-3)]">
              <span>{report.delivery_count} 件</span>
              {reportDistanceKm(report) && <span>{reportDistanceKm(report)} km</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ResubmitReportForm({
  fetcher,
  report,
  onCancel,
  onDone,
}: {
  fetcher: DriverFetcher;
  report: DriverDailyReportDto;
  onCancel: () => void;
  onDone: (updated: DriverDailyReportDto) => void;
}) {
  const [deliveryCount, setDeliveryCount] = useState(String(report.delivery_count));
  const [distance, setDistance] = useState(report.total_distance_km ?? "");
  const [remarks, setRemarks] = useState(report.remarks ?? "");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit() {
    setError(null);
    if (!deliveryCount) {
      setError("配送件数を入力してください");
      return;
    }
    setSubmitting(true);
    try {
      const updated = await resubmitDriverDailyReport(fetcher, report.public_id, {
        delivery_count: Number(deliveryCount),
        total_distance_km: distance.trim() || undefined,
        remarks: remarks.trim(),
      });
      onDone(updated);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "再提出に失敗しました");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-[var(--bg)] pb-[var(--bottom-bar-clearance)]">
      <Header
        title="日報の修正・再提出"
        sub={formatDisplayDate(report.work_date)}
        showBack
        onBack={onCancel}
      />
      <form
        className="flex flex-col gap-3 px-3.5 py-3.5"
        onSubmit={(event) => {
          event.preventDefault();
          void onSubmit();
        }}
      >
        <div className="rounded-lg bg-[var(--st-cancel-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-cancel-ink)]">
          <div className="font-semibold">差戻し理由</div>
          <div className="mt-1">{report.rejection_note ?? "理由の記載はありません"}</div>
        </div>
        <label className="flex flex-col gap-1 text-[length:var(--fs-xs)] text-[var(--ink-2)]">
          配送件数
          <Input
            type="number"
            inputMode="numeric"
            min={0}
            required
            value={deliveryCount}
            onChange={(event) => setDeliveryCount(event.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1 text-[length:var(--fs-xs)] text-[var(--ink-2)]">
          走行距離 (km)
          <Input
            type="text"
            inputMode="decimal"
            value={distance}
            onChange={(event) => setDistance(event.target.value)}
          />
        </label>
        <label className="flex flex-col gap-1 text-[length:var(--fs-xs)] text-[var(--ink-2)]">
          特記事項
          <Textarea value={remarks} onChange={(event) => setRemarks(event.target.value)} rows={4} />
        </label>
        <p className="text-[length:var(--fs-xs)] text-[var(--ink-3)]">
          点呼・点検・アルコール検査は再提出時に最新記録を反映し、承認後は確定値として保持します。
        </p>
        {error && (
          <div
            role="alert"
            className="rounded-lg bg-[var(--st-cancel-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-cancel-ink)]"
          >
            ⚠ {error}
          </div>
        )}
        <Button type="submit" disabled={submitting || !deliveryCount} className="mt-1 w-full">
          {submitting ? "再提出中…" : "再提出する"}
        </Button>
      </form>
    </div>
  );
}

function RecoveryCandidateSummary({
  candidate,
  compact = false,
}: {
  candidate: DailyReportRecoveryCandidateDto;
  compact?: boolean;
}) {
  const attendance = `${formatHm(candidate.clock_in_at)}〜${
    candidate.clock_out_at ? formatHm(candidate.clock_out_at) : "未退勤"
  }`;
  return (
    <div className={compact ? "space-y-1.5" : ""}>
      <div className="flex items-start justify-between gap-2">
        <div className="font-semibold">{formatDisplayDate(candidate.work_date)}</div>
        <div className="text-right text-[length:var(--fs-xs)] text-[var(--ink-3)]">
          {attendance}
        </div>
      </div>
      <div className="mt-1 text-[length:var(--fs-xs)] text-[var(--ink-2)]">
        車両: {candidate.vehicle_number ?? "未割当"}
      </div>
      <div className="mt-1 flex flex-wrap gap-1">
        {candidate.routes.map((route) => (
          <span
            key={route.public_id}
            className="rounded bg-[var(--bg-sunken)] px-1.5 py-0.5 text-[length:var(--fs-xs)] text-[var(--ink-2)]"
          >
            {/* #425: 日単位レーンの time_slot は代表便の導出値で null になりうる。null 時は便ラベルを出さない */}
            {route.time_slot != null && <>{timeSlotLabel(route.time_slot)} · </>}
            {routeStatusLabel(route.status)}
          </span>
        ))}
      </div>
      <div className="mt-1.5 flex gap-4 text-[length:var(--fs-xs)] text-[var(--ink-3)]">
        <span>{candidate.delivery_count} 件</span>
        <span>
          走行距離: {candidate.total_distance_km ? `${candidate.total_distance_km} km` : "未取得"}
        </span>
      </div>
    </div>
  );
}

function CreatePastReportForm({
  fetcher,
  month,
  onCancel,
  onDone,
}: {
  fetcher: DriverFetcher;
  month: string;
  onCancel: () => void;
  onDone: (report: DriverDailyReportDto) => void;
}) {
  const [selectedCandidate, setSelectedCandidate] =
    useState<DailyReportRecoveryCandidateDto | null>(null);
  const [remarks, setRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submittingRef = useRef(false);
  const {
    candidates,
    loading,
    error: candidatesError,
    refetch: refetchCandidates,
  } = useDriverDailyReportRecoveryCandidates({
    ...fetcher,
    month,
  });

  async function onSubmit() {
    if (!selectedCandidate || submittingRef.current) return;
    submittingRef.current = true;
    setError(null);
    setSubmitting(true);
    try {
      const report = await recoverDriverDailyReport(fetcher, {
        work_date: selectedCandidate.work_date,
        ...(remarks.trim() ? { remarks: remarks.trim() } : {}),
      });
      await refetchCandidates();
      onDone(report);
    } catch (cause) {
      if (isDriverDailyReportAlreadyExistsError(cause)) {
        try {
          const existing = await getDriverDailyReport(
            fetcher,
            cause.data.existing_report.public_id,
          );
          await refetchCandidates();
          onDone(existing);
          return;
        } catch (getCause) {
          setError(getCause instanceof Error ? getCause.message : "既存日報の取得に失敗しました");
        }
      } else {
        setError(cause instanceof Error ? cause.message : "作成に失敗しました");
      }
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  }

  if (!selectedCandidate) {
    return (
      <div className="min-h-screen bg-[var(--bg)] pb-[var(--bottom-bar-clearance)]">
        <Header title="過去日報の作成" sub={month} showBack onBack={onCancel} />
        <div className="px-3.5 py-3.5">
          <h2 className="text-[length:var(--fs-sm)] font-semibold">作成する日報を選択</h2>
          <p className="mt-1 text-[length:var(--fs-xs)] text-[var(--ink-3)]">
            出退勤・車両・配送実績から自動算出できる日のみ表示します。作成後、未完了の配送があれば詳細から実績を登録できます。
          </p>
          {loading && (
            <div className="p-4 text-center text-[length:var(--fs-xs)] text-[var(--ink-3)]">
              読込中…
            </div>
          )}
          {candidatesError && (
            <div
              role="alert"
              className="mt-3 rounded-lg bg-[var(--st-cancel-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-cancel-ink)]"
            >
              ⚠ {candidatesError}
            </div>
          )}
          {!loading && !candidatesError && candidates?.length === 0 && (
            <div className="p-4 text-center text-[length:var(--fs-xs)] text-[var(--ink-3)]">
              作成が必要な過去日報はありません
            </div>
          )}
          <div className="mt-3 space-y-2">
            {candidates?.map((candidate) => (
              <button
                key={candidate.work_date}
                type="button"
                aria-label={`${formatDisplayDate(candidate.work_date)}の日報を選択`}
                onClick={() => {
                  setError(null);
                  setRemarks("");
                  setSelectedCandidate(candidate);
                }}
                className="w-full cursor-pointer rounded-lg border border-[var(--rule-soft)] bg-[var(--bg-raised)] px-3.5 py-3 text-left shadow-sm"
              >
                <RecoveryCandidateSummary candidate={candidate} compact />
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--bg)] pb-[var(--bottom-bar-clearance)]">
      <Header
        title="過去日報の作成"
        sub={formatDisplayDate(selectedCandidate.work_date)}
        showBack
        onBack={() => {
          if (submittingRef.current) return;
          setError(null);
          setSelectedCandidate(null);
        }}
      />
      <form
        className="flex flex-col gap-3 px-3.5 py-3.5"
        onSubmit={(event) => {
          event.preventDefault();
          void onSubmit();
        }}
      >
        <h2 className="text-[length:var(--fs-sm)] font-semibold">自動算出内容の確認</h2>
        <div className="rounded-lg bg-[var(--bg-raised)] px-3.5 py-3 shadow-sm">
          <RecoveryCandidateSummary candidate={selectedCandidate} />
        </div>
        <label className="flex flex-col gap-1 text-[length:var(--fs-xs)] text-[var(--ink-2)]">
          特記事項 (任意)
          <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={3} />
        </label>
        {error && (
          <div
            role="alert"
            className="rounded-lg bg-[var(--st-cancel-bg)] px-3 py-2 text-[length:var(--fs-xs)] text-[var(--st-cancel-ink)]"
          >
            ⚠ {error}
          </div>
        )}
        <Button type="submit" disabled={submitting} className="mt-1 w-full">
          {submitting ? "作成中…" : "日報を作成"}
        </Button>
      </form>
    </div>
  );
}
